import { DiscoThemeProvider } from "@disco-ui"
import { useTheme } from "@material-ui/core"
import classNames from "classnames"
import React from "react"
import ReactModal, { Props as ReactModalProps } from "react-modal"
import classes from "./Modal.module.css"
import { MODAL_CLOSE_TIMEOUT } from "./util/modalConstants"

export type ModalProps = {
  isOpen: boolean
  modalContentLabel: string
  onClose?: (...args: any[]) => void
  onAfterOpen?: (...args: any[]) => void
  onAfterClose?: (...args: any[]) => void
  closeTimeout?: number
  shouldCloseOnOverlayClick?: boolean
  customClassName?: string
  overlayClassName?: string
  children?: React.ReactNode
  shouldCloseOnEsc?: boolean
  bodyOpenClassName?: string
  portalClassName?: string
  disableTransition?: boolean
  /** Whether the modal is on top of another modal, this raises the z-index pf the modal by 1 */
  nested?: boolean
  unstyled?: boolean
} & Pick<ReactModalProps, "parentSelector" | "contentRef">

ReactModal.defaultStyles = {}
ReactModal.setAppElement("#root")

function getParent() {
  return document.querySelector("#modal-root") as HTMLElement
}

function Modal({
  isOpen,
  customClassName,
  children,
  closeTimeout = MODAL_CLOSE_TIMEOUT,
  modalContentLabel,
  onAfterClose,
  onAfterOpen,
  overlayClassName,
  shouldCloseOnOverlayClick = true,
  shouldCloseOnEsc = true,
  bodyOpenClassName,
  onClose,
  portalClassName,
  parentSelector,
  disableTransition = false,
  nested = false,
  unstyled = false,
  contentRef,
}: ModalProps) {
  const containerClassName = classNames(classes.container, customClassName)
  const bodyClassName = classNames("ReactModal__Body--open", bodyOpenClassName)
  const theme = useTheme()

  return (
    // This wrapper div and click handler is added to stop any click events from bubbling outside of the modal
    // eslint-disable-next-line
    <div style={{ position: "absolute" }} onClick={(event) => event.stopPropagation()}>
      <ReactModal
        contentRef={contentRef}
        parentSelector={parentSelector || getParent}
        isOpen={isOpen}
        onAfterOpen={onAfterOpen}
        onAfterClose={onAfterClose}
        contentLabel={modalContentLabel}
        className={containerClassName}
        portalClassName={classNames(
          classes.portal,
          {
            [classes.nestedModal]: nested,
            [classes.transition]: !disableTransition,
          },
          portalClassName
        )}
        onRequestClose={handleRequestClose}
        overlayClassName={overlayClassName}
        closeTimeoutMS={closeTimeout}
        bodyOpenClassName={bodyClassName}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        shouldCloseOnEsc={shouldCloseOnEsc}
        data-theme={"light"}
        overlayElement={(overlayProps, contentEl) => (
          // Modal content gets portaled outside of the main app in the DOM and doesn't
          // inherit its theme CSS vars, so wrap the content in its own theme provider
          <DiscoThemeProvider theme={theme} unstyled={unstyled}>
            <div {...overlayProps} data-theme={unstyled && "light"}>
              {contentEl}
            </div>
          </DiscoThemeProvider>
        )}
      >
        {children}
      </ReactModal>
    </div>
  )

  function handleRequestClose() {
    if (onClose && (shouldCloseOnOverlayClick || shouldCloseOnEsc)) {
      onClose()
    }
  }
}

export default Modal
